<template lang="pug">
.bq-signup
    //- router-link.bq-logo(to="/") ₍ᐢ•⩊•ᐢ₎
    .bq-login-link
        router-link(to="/")
            SkapiBunnyLockup
        br
        br
        span You can use your Skapi account to&nbsp;
        RouterLink.bq-link(data-track="signup_login_top" :to="{name: 'login'}" :class='{disabled: promiseRunning}') Login &rarr;

        br
        br
        span Already have an account?&nbsp;
        RouterLink.bq-link(data-track="signup_login_existing" :to="{name: 'login'}" :class='{disabled: promiseRunning}') Login &rarr;
    
    h1.bq-title Sign up

    form.bq-form(@submit.prevent="signup" autocomplete="off")
        label.bq-label
            span Email
            input(type="email" :value='form.email' @input="e=> { form.email = e.target.value; }" :disabled="promiseRunning" placeholder="your@email.com" autocomplete="off" required)

        label.bq-label
            span Name
            input(type="text" :value='form.name' @input="e=> { form.name = e.target.value; }" :disabled="promiseRunning" :placeholder="randomNamePlaceholder" autocomplete="off" required)

        label.bq-label
            span Create password
            input(type="password" @input="e=> { form.password = e.target.value.trim(); e.target.setCustomValidity(''); error = '' }" :disabled="promiseRunning" minlength="6" maxlength="60" placeholder="At least 6 characters" autocomplete="new-password" required)

        label.bq-label
            span Confirm password
            input(type="password" ref="confirmPasswordField" @input="e=> { form.password_confirm = e.target.value.trim(); e.target.setCustomValidity(''); error = '' }" @change="validatePassword" :disabled="promiseRunning" placeholder="Enter your password again" autocomplete="new-password" required)

        .bq-actions
            Checkbox(style='font-weight:unset;' :disabled='promiseRunning' v-model='form.subscribe') I agree to receive newsletters
        
        .bq-error(v-if="openIdLoginError || error")
            span {{ openIdLoginError || error }}

        .bq-bottom
            button.bq-btn(type="submit" data-track="signup_signup" :class="{ 'bq-btn--loading': promiseRunning }")
                span.bq-loader(v-if="promiseRunning")
                template(v-else) Sign up

    .bq-divider
        .bq-divider-line
        span.bq-divider-text OR
        .bq-divider-line

    button.bq-google(@click="e=>googleLogin(e,'signup_googleLogin')" :disabled="promiseRunning" data-track="signup_googleLogin")
        svg(viewBox="0 0 48 48" style="width:20px;height:20px;flex:none;")
            path(fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z")
            path(fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z")
            path(fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z")
            path(fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z")
            path(fill="none" d="M0 0h48v48H0z")
        span Continue with Google

</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { SkapiError } from "skapi-js";
import { skapi, onLoginHanger } from "@/main";
import { openIdLoginError } from "@/oauth";
import { googleLogin, handleGoogleOAuthReturn } from "@/oauth";
import Checkbox from "@/components/checkbox.vue";
import { removeFullScreenLoader } from "@/components/jsComponents";
import SkapiBunnyLockup from "@/components/skapiBunnyLockup.vue";
import { user } from "@/code/user";
import {
    beginMcpOAuthOnLogin,
    completeMcpAuthorize,
    isMcpOAuthCallback,
} from "@/code/mcp_oauth";
import { signupTemplateOption } from "@/code/auto_email";

onLoginHanger["signup.vue"] = async (u) => {
    // If we're returning from MCP /oauth/authorize, complete the token
    // exchange first and bounce to the originally-intended page. This runs
    // before the my-projects redirect so the ?code/state querystring isn't
    // mistaken for an unrelated OAuth flow.
    const q = route.query || {};
    if (isMcpOAuthCallback(q as any)) {
        try {
            const { redirectAfter } = await completeMcpAuthorize(q as any);
            router.push(redirectAfter || "/my-projects");
            return;
        } catch (err) {
            console.error("MCP OAuth token exchange failed", err);
            error.value = "Failed to complete MCP authorization.";
            removeFullScreenLoader();
            return;
        }
    }

    let bound = await handleGoogleOAuthReturn();

    if(bound !== 'redirecting') {
        if(u?.user_id) {
            router.push("/my-projects");
        }
        else {
            removeFullScreenLoader();
        }
    }
};
const router = useRouter();
const route = useRoute();

let promiseRunning = ref(false);
let confirmPasswordField = ref(null);
let error = ref(null);
let form = ref({
    email: "",
    name: "",
    password: "",
    password_confirm: "",
    subscribe: true,
});
let randomNamePlaceholder = (() => {
    const names = [
        "Ada Lovelace",
        "Grace Hopper",
        "Jean Jennings Bartik",
        "Frances E. Allen",
        "Mary Kenneth Keller",
        "Erna Schneider Hoover",
        "Karen Sparck Jones",
        "Dina St Johnston",
        "Evelyn Boyd Granville",
        "Dana Scott",
        "Betty Holberton",
        "Marlyn Wescoff Meltzer",
        "Ruth Lichterman Teitelbaum",
        "Frances Bilas Spence",
        "Kathleen Booth",
        "Mary Lee Woods",
        "Ida Rhodes",
        "Joan Clarke",
        "Lois Haibt",
        "Lynn Conway"
    ];
    return names[Math.floor(Math.random() * names.length)];
})();

let validatePassword = () => {
    if (form.value.password_confirm !== form.value.password) {
        if (confirmPasswordField.value) {
            confirmPasswordField.value.setCustomValidity("Password does not match");
            confirmPasswordField.value.reportValidity();
        }
    }
};

onMounted(() => {
    if(onLoginHanger["signup.vue"]) {
        onLoginHanger["signup.vue"](user);
        delete onLoginHanger["signup.vue"];
    }
});

let signup = (e) => {
    if (promiseRunning.value) return;
    error.value = "";
    promiseRunning.value = true;

    let params = {
        email: form.value.email,
        name: form.value.name,
        password: form.value.password,
    };

    let options: any = {
        signup_confirmation: "/signup-success?e=" + encodeURIComponent(form.value.email),
        email_subscription: form.value.subscribe,
    };

    // Pull signup_confirmation + welcome template message_ids from
    // VITE_AUTO_EMAIL (if configured) so uploaded templates are used.
    const tplOpt = signupTemplateOption();
    if (tplOpt) options.template = tplOpt.template;

    skapi
        .signup(params, options)
        .then((res) => {
            router.push({
                path: "/signup-confirmation",
                query: { email: form.value.email },
            });
        })
        .catch((err) => {
            promiseRunning.value = false;

            // SkapiError가 아니면 throw
            if (!(err instanceof SkapiError)) throw err;

            // 기타 에러 처리
            switch (err.code) {
                case "EXISTS":
                case "UsernameExistsException":
                    error.value = "This email is already in use";
                    break;
                default:
                    error.value =
                        "Something went wrong please contact an administrator.";
                    throw err;
            }
        });
};
</script>

<style scoped lang="less">
@import "@/assets/less/bq-tokens.less";

.bq-signup {
    max-width: 440px;
    padding: 3rem 1.5rem;
    margin: 0 auto;
    font-family: @mono;
    color: @ink;
}

.bq-logo {
    font-size: 1.2rem;
    color: @ink;
    text-decoration: none;
    letter-spacing: 0.04em;

    &:hover {
        text-decoration: none;
    }
}

.bq-title {
    font-size: 1.6rem;
    font-weight: 700;
    margin: 2rem 0 1.5rem;
    letter-spacing: -0.01em;
}

/* ─── form ─── */
.bq-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.bq-label {
    display: flex;
    flex-direction: column;
    // gap: 0.4rem;
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.bq-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: 0.82rem;
}

.bq-link {
    font-size: 0.82rem;
}

/* ─── error ─── */
.bq-error {
    padding: 0.75rem 1rem;
    border: 1px solid @danger;
    background: @danger-bg;
    color: @danger;
    font-size: 0.82rem;
    line-height: 1.5;
}

/* ─── bottom ─── */
.bq-bottom {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin-top: 0.5rem;
    text-align: center;
}

.bq-login-link {
    font-size: 0.82rem;
    color: @muted;
}

/* ─── divider ─── */
.bq-divider {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 2rem 0;
}

.bq-divider-line {
    flex: 1;
    height: 1px;
    background: @line;
}

.bq-divider-text {
    font-size: 0.72rem;
    color: @muted;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

/* ─── google button ─── */
.bq-google {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
    max-width: none;
    height: auto;
    min-height: 0;
    padding: 0.75rem 1rem;
    border: 1px solid @line;
    background: @paper;
    font-family: @mono;
    font-size: 0.88rem;
    color: @ink;
    cursor: pointer;
    border-radius: 0;
    box-sizing: border-box;
    transition: border-color 0.15s, background 0.15s;
    box-shadow: 2px 2px rgba(0, 0, 0, 0.12);

    &::after {
        display: none;
    }

    &:hover {
        border-color: @ink;
        background: @paper;
    }

    &:disabled {
        opacity: 0.3;
        pointer-events: none;
    }
}

/* ─── mobile ─── */
@media (max-width: 480px) {
    .bq-signup {
        padding: 2rem 1.25rem;
    }

    .bq-bottom {
        flex-direction: column-reverse;
        align-items: stretch;
        text-align: center;
    }
}
</style>
